import { useEffect, useState } from "react";
import { Loader2, Plus, Sparkles, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiGet, apiPost } from "@/lib/apiAdmin";
import { Switch } from "@/components/ui/switch";
import { BUTTON_TYPES } from "@/components/landing/HeroLetters";
import { HUD } from "./theme";

const inp = `h-9 rounded-lg px-3 text-sm border ${HUD.input}`;

// Homepage headline: edit the two lines, get 5 AI ideas, or let JARVIS write a new one every day
export const HeadlineEditor = ({ v, setV }) => {
  const [ideas, setIdeas] = useState([]);
  const [busy, setBusy] = useState(false);
  const ask = () => { setBusy(true); apiPost("/admin/hero/headlines", {}).then((r) => setIdeas(r.headlines)).catch((e) => toast.error(e?.response?.data?.detail || "Try again")).finally(() => setBusy(false)); };
  return (
    <div className="space-y-2" data-testid="lettering-headline">
      <p className={HUD.label}>Headline</p>
      <div className="flex flex-wrap gap-2">
        <input value={v.line1} onChange={(e) => setV({ ...v, line1: e.target.value })} maxLength={30} placeholder="Line 1 (e.g. Showroom)" className={`${inp} flex-1 min-w-[140px]`} data-testid="lettering-line1" />
        <input value={v.line2} onChange={(e) => setV({ ...v, line2: e.target.value })} maxLength={30} placeholder="Line 2, highlighted (e.g. Detailing)" className={`${inp} flex-1 min-w-[140px]`} data-testid="lettering-line2" />
        <button onClick={ask} disabled={busy} className={`${HUD.ghost} h-9 px-3 text-xs flex items-center gap-1.5`} data-testid="lettering-ai-ideas">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}AI ideas
        </button>
      </div>
      {ideas.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="lettering-ai-list">
          {ideas.map((h, i) => (
            <button key={i} onClick={() => setV({ ...v, line1: h.line1, line2: h.line2 })} className="h-8 px-3 rounded-full text-xs border border-cyan-400/20 text-gray-200 hover:border-cyan-300" data-testid={`lettering-ai-idea-${i}`}>
              {h.line1} <span className="text-cyan-300">{h.line2}</span>
            </button>
          ))}
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-gray-300">
        <Switch checked={v.daily} onCheckedChange={(on) => setV({ ...v, daily: on })} data-testid="lettering-ai-daily" />JARVIS writes a fresh headline every day
      </label>
    </div>
  );
};

export const ButtonsEditor = ({ v, setV }) => {
  const set = (i, b) => setV({ ...v, buttons: v.buttons.map((x, j) => (j === i ? b : x)) });
  return (
    <div className="space-y-2" data-testid="lettering-buttons">
      <p className={HUD.label}>Buttons around the car (up to 5)</p>
      {v.buttons.map((b, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`lettering-button-${i}`}>
          <select value={b.type} onChange={(e) => set(i, { type: e.target.value })} className={inp} data-testid={`lettering-button-type-${i}`}>
            {BUTTON_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          {b.type !== "book" && <input value={b.label || ""} onChange={(e) => set(i, { ...b, label: e.target.value.slice(0, 24) })} placeholder="Button text (optional)" className={`${inp} w-44`} data-testid={`lettering-button-label-${i}`} />}
          {b.type === "custom" && <input value={b.url || ""} onChange={(e) => set(i, { ...b, url: e.target.value.slice(0, 300) })} placeholder="Link, e.g. /portfolio or https://…" className={`${inp} flex-1 min-w-[160px]`} data-testid={`lettering-button-url-${i}`} />}
          <button onClick={() => setV({ ...v, buttons: v.buttons.filter((_, j) => j !== i) })} className="text-gray-500 hover:text-red-400" data-testid={`lettering-button-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {v.buttons.length < 5 && (
        <button onClick={() => setV({ ...v, buttons: [...v.buttons, { type: "build" }] })} className={`${HUD.ghost} h-8 px-3 text-xs flex items-center gap-1.5`} data-testid="lettering-button-add"><Plus className="w-3.5 h-3.5" />Add button</button>
      )}
    </div>
  );
};

// Re-frames the car where you placed it and sharpens to 4K, then makes that the homepage photo
export const CleanupButton = ({ src, carPos, onDone }) => {
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!job) return undefined;
    const t = setInterval(() => apiGet(`/admin/hero/jobs/${job}`).then((j) => {
      if (j.status === "running") return;
      setJob(null);
      if (j.status === "failed") return toast.error(j.error || "Clean-up failed");
      api.put("/admin/site/content", { "hero.mode": "image", "hero.bg_url": j.media.url, "hero.bg_kind": "image", "hero.car_pos": "" })
        .then(() => { toast.success("Sharp, cleaned-up photo is now on your homepage"); window.dispatchEvent(new CustomEvent("hero-mode", { detail: "image" })); onDone?.(); });
    }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = () => apiPost("/admin/hero/cleanup", { src, car_pos: carPos }).then((j) => setJob(j.job_id)).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't start"));
  return (
    <button onClick={go} disabled={!!job || !src} className={`${HUD.btn} h-9 px-4 text-xs flex items-center gap-2 disabled:opacity-50`} data-testid="lettering-cleanup">
      {job ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}{job ? "Cleaning up… (about 1 min)" : "Clean up & sharpen (AI)"}
    </button>
  );
};
